/**
 * Keep the React tree alive under the browser's automatic page translation.
 *
 * Chrome's built-in translator (Google Translate) rewrites the page in place:
 * every text node it translates is replaced by
 * `<font style="vertical-align: inherit"><font …>перевод</font></font>`. React
 * still holds the ORIGINAL text node, so on its next update of that spot it
 * calls `parent.removeChild(oldText)` or `parent.insertBefore(x, oldText)` on a
 * node that is no longer a child there — the DOM throws `NotFoundError`, and
 * the whole route falls into the «Страница не открылась» boundary
 * (facebook/react#11538, open since 2017). A conditional `{cond && 'text'}`
 * flipping after a click is enough to trigger it.
 *
 * The guard turns exactly those two impossible operations into no-ops:
 *  - `removeChild(child)` when `child` is not a child of this node → return
 *    `child` untouched (it is already gone from where React thinks it is);
 *  - `insertBefore(node, ref)` when `ref` is not a child of this node → append
 *    `node` at the end instead (the position is approximate, the page survives).
 * Every legitimate call goes to the native method unchanged. One `console.warn`
 * per page load says that it happened.
 *
 * Browser only: call it once from `entry-client.tsx`, before React mounts. It
 * never runs under SSR/prerender (no `Node` there), and installing twice wraps
 * once — the mark lives on `Node.prototype` itself, so even a second copy of
 * this module (HMR) cannot stack a second wrapper.
 */

const INSTALLED = Symbol.for('uztender.translateGuard');

type GuardedProto = Node & { [INSTALLED]?: true };

let warned = false;

function warnOnce(method: 'removeChild' | 'insertBefore'): void {
  if (warned) return;
  warned = true;
  console.warn(
    `[translate-guard] ${method} on a node that is no longer a child — the page was changed ` +
      'outside React (most likely Google Translate / browser page translation). Ignored instead ' +
      'of crashing the page; see facebook/react#11538.',
  );
}

export function installTranslateGuard(): void {
  if (typeof Node === 'undefined' || !Node.prototype) return;
  const proto = Node.prototype as GuardedProto;
  if (proto[INSTALLED]) return;
  Object.defineProperty(proto, INSTALLED, { value: true });

  const nativeRemoveChild = proto.removeChild;
  proto.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child && child.parentNode !== this) {
      warnOnce('removeChild');
      return child;
    }
    return nativeRemoveChild.call(this, child) as T;
  };

  const nativeInsertBefore = proto.insertBefore;
  proto.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      warnOnce('insertBefore');
      return this.appendChild(node);
    }
    return nativeInsertBefore.call(this, node, referenceNode) as T;
  };
}

/** Test seam — let the next guarded call warn again. */
export function resetTranslateGuardWarning(): void {
  warned = false;
}
